import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/LoginPage';
import DashboardLayout from './layouts/DashboardLayout';
import { AuthProvider } from './contexts/AuthContext';
import { usePermissions } from './hooks/usePermissions';
import UsersManagementPage from './pages/UsersManagementPage';
import UserEditorPage from './pages/UserEditorPage';
import SearchOrdersPageB24 from './pages/SearchOrdersPageB24';
import Visualloft from './pages/Visualloft';
import CuttingOrdersPageB24 from './pages/CuttingOrdersPageB24';

// Импорт новых B24 страниц
import DrillingOrdersPageB24 from './pages/DrillingOrdersPageB24';
import MatovkaOrdersPageB24 from './pages/MatovkaOrdersPageB24';
import ZakalkaOrdersPageB24 from './pages/ZakalkaOrdersPageB24';
import ZakalkaTripsPage from './pages/ZakalkaTripsPage';
import ScladOrdersPageB24 from './pages/ScladOrdersPageB24';
import ArgonOrdersPageB24 from './pages/ArgonOrdersPageB24';
import UpakOrdersPageB24 from './pages/UpakOrdersPageB24';
import LoftOrdersPageB24 from './pages/LoftOrdersPageB24';
import DriverSummaryPageB24 from './pages/DriverSummaryPageB24';
import ZakalkaDriverTripsPage from './pages/ZakalkaDriverTripsPage';
import SendOrdersPageB24 from './pages/SendOrdersPageB24';
import PickupOrdersPageB24 from './pages/PickupOrdersPageB24';

import TripManagementPage from './pages/TripManagementPage';
import OrdersDashboardPage from './pages/OrdersDashboardPage';
import TVDashboardPage from './pages/TVDashboardPage';
import FilePrintPage from './pages/FilePrintPage';
import MediaAdminPage from './pages/MediaAdminPage';
import NewsPage from './pages/NewsPage';
import OrderFormPage from './pages/OrderFormPage';
import ReportsMainPage from './pages/ReportsMainPage';
import BuhgalteriyaPage from './pages/BuhgalteriyaPage';

// Компонент для защищенных маршрутов с поддержкой разрешений
const ProtectedRoute = ({ children, requiredPermission = null, allowedRoles = [] }) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  try {
    return (
      <PermissionWrapper requiredPermission={requiredPermission} allowedRoles={allowedRoles}>
        {children}
      </PermissionWrapper>
    );
  } catch (error) {
    console.error('ProtectedRoute error:', error);
    return <Navigate to="/login" replace />;
  }
};

// Внутренний компонент для проверки разрешений
const PermissionWrapper = ({ children, requiredPermission, allowedRoles }) => {
  try {
    const { hasPermission, user, isLoading } = usePermissions();
    
    // Показываем загрузку пока проверяются разрешения
    if (isLoading) {
      return <div className="p-4">Проверка разрешений...</div>;
    }
    
    // Проверяем наличие пользователя
    if (!user || !user.role) {
      return <Navigate to="/login" replace />;
    }
    
    // Если требуется конкретное разрешение, проверяем его
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return <Navigate to={getDefaultPageForUser(user)} replace />;
    }

    // Обратная совместимость с системой ролей
    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
      return <Navigate to={getDefaultPageForUser(user)} replace />;
    }

    return children;
  } catch (error) {
    console.error('PermissionWrapper error:', error);
    return <Navigate to="/login" replace />;
  }
};

// Функция для определения страницы по умолчанию для пользователя
const getDefaultPageForUser = (user) => {
  try {
    // Проверяем наличие пользователя
    if (!user || !user.role) {
      return '/login';
    }
    
    // Получаем разрешения пользователя напрямую из localStorage
    const userPermissions = user.permissions || getDefaultPermissions(user.role);
    
    // Убеждаемся что userPermissions это массив
    if (!Array.isArray(userPermissions)) {
      console.error('userPermissions is not an array:', userPermissions);
      return '/login';
    }
    
    // Приоритет страниц для перенаправления
    const pagePriority = [
      { path: '/users', permission: 'users' },
      { path: '/reports', permission: 'reports' },
      { path: '/orders-dashboard', permission: 'orders-dashboard' },
      { path: '/search', permission: 'search-cloud' },
      { path: '/search-b24', permission: 'search-b24' },
      { path: '/cutting', permission: 'cutting' },
      { path: '/cuttingb24', permission: 'cuttingb24' },
      { path: '/drilling', permission: 'drilling' },
      { path: '/drillingb24', permission: 'drillingb24' },
      { path: '/zakalka', permission: 'zakalka' },
      { path: '/zakalkab24', permission: 'zakalkab24' },
      { path: '/matovka', permission: 'matovka' },
      { path: '/matovkab24', permission: 'matovkab24' },
      { path: '/sclad', permission: 'sclad' },
      { path: '/scladb24', permission: 'scladb24' },
      { path: '/argonb24', permission: 'argonb24' },
      { path: '/loft', permission: 'loft' },
      { path: '/loftb24', permission: 'loftb24' },
      { path: '/upak', permission: 'upak' },
      { path: '/upakb24', permission: 'upakb24' },
      { path: '/pickupb24', permission: 'pickupb24' },
      { path: '/sendb24', permission: 'sendb24' },
      { path: '/zakalka-driver-trips', permission: 'driverb24' },
      { path: '/visualloft', permission: 'visualloft' },
      { path: '/file-print', permission: 'file-print' },
      { path: '/order-form', permission: 'order-form' },
      { path: '/buhgalteriya', permission: 'buhgalteriya' },
    ];
    
    // Находим первую доступную страницу
    for (const page of pagePriority) {
      if (userPermissions.includes(page.permission)) {
        return page.path;
      }
    }
    
    // Если ничего не найдено, перенаправляем на логин
    return '/login';
  } catch (error) {
    console.error('getDefaultPageForUser error:', error);
    return '/login';
  }
};

// Функция получения разрешений по умолчанию (дублируем из хука)
const getDefaultPermissions = (role) => {
  const defaultPerms = {
    'admin': ['search-cloud', 'search-b24', 'cutting', 'drilling', 'zakalka', 'matovka', 'sclad', 'loft', 'upak',
              'cuttingb24', 'drillingb24', 'zakalkab24', 'matovkab24', 'scladb24', 'argonb24', 'loftb24',
              'upakb24', 'pickupb24', 'sendb24', 'driverb24', 'trip-management', 'visualloft', 'product-variations',
              'tovar', 'users', 'facetherminal', 'orders-dashboard', 'file-print', 'order-form', 'reports', 'buhgalteriya'],
    'adminb24': ['search-cloud', 'search-b24', 'cutting', 'drilling', 'zakalka', 'matovka', 'sclad', 'loft', 'upak',
                 'cuttingb24', 'drillingb24', 'zakalkab24', 'matovkab24', 'scladb24', 'argonb24', 'loftb24',
                 'upakb24', 'pickupb24', 'sendb24', 'driverb24', 'trip-management', 'visualloft', 'product-variations',
                 'tovar', 'users', 'facetherminal', 'orders-dashboard', 'file-print', 'order-form', 'reports', 'buhgalteriya'],
    'cutter': ['search-cloud', 'cutting'],
    'driller': ['search-cloud', 'drilling'],
    'zakalka': ['search-cloud', 'zakalka'],
    'matovka': ['search-cloud', 'matovka'],
    'sclad': ['search-cloud', 'sclad'],
    'loft': ['search-cloud', 'loft'],
    'upak': ['search-cloud', 'upak'],
    'cutterb24': ['search-b24', 'cuttingb24'],
    'drillerb24': ['search-b24', 'drillingb24'],
    'zakalkab24': ['search-b24', 'zakalkab24'],
    'matovkab24': ['search-b24', 'matovkab24'],
    'scladb24': ['search-b24', 'scladb24'],
    'loftb24': ['search-b24', 'loftb24'],
    'upakb24': ['search-b24', 'upakb24'],
    'sendb24': ['search-b24', 'sendb24'],
    'driverb24': ['search-b24', 'driverb24'],
    'designer': ['search-cloud', 'search-b24', 'visualloft'],
    'franchise': ['search-b24', 'visualloft'],
    'франчайзи': ['search-b24'],
    'buhgalter': ['buhgalteriya'],
    'director': ['search-b24', 'buhgalteriya'],
    'initiator': ['buhgalteriya'],
  };
  return defaultPerms[role] || ['search-cloud', 'search-b24'];
};

// Компонент для автоматического перенаправления на основе разрешений
const RoleBasedRedirect = () => {
  try {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    return <Navigate to={getDefaultPageForUser(user)} replace />;
  } catch (error) {
    console.error('RoleBasedRedirect error:', error);
    return <Navigate to="/login" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          
          {/* TV Dashboard без сайдбаров */}
          <Route path="/tv-dashboard" element={<TVDashboardPage />} />
          
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Корневой маршрут с перенаправлением по роли */}
            <Route index element={<RoleBasedRedirect />} />
            
            {/* Маршруты для поиска */}
            <Route
              path="search-b24"
              element={
                <ProtectedRoute requiredPermission="search-b24">
                  <SearchOrdersPageB24 />
                </ProtectedRoute>
              }
            />

            {/* Новые B24 маршруты */}
            <Route 
              path="cuttingb24" 
              element={
                <ProtectedRoute requiredPermission="cuttingb24">
                  <CuttingOrdersPageB24 />
                </ProtectedRoute>
              }
            />

            <Route 
              path="drillingb24" 
              element={
                <ProtectedRoute requiredPermission="drillingb24">
                  <DrillingOrdersPageB24 />
                </ProtectedRoute>
              }
            />

            <Route
              path="zakalkab24"
              element={
                <ProtectedRoute requiredPermission="zakalkab24">
                  <ZakalkaOrdersPageB24 />
                </ProtectedRoute>
              }
            />

            <Route
              path="zakalka-trips"
              element={
                <ProtectedRoute requiredPermission="zakalkab24">
                  <ZakalkaTripsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="matovkab24" 
              element={
                <ProtectedRoute requiredPermission="matovkab24">
                  <MatovkaOrdersPageB24 />
                </ProtectedRoute>
              }
            />

            <Route 
              path="loftb24" 
              element={
                <ProtectedRoute requiredPermission="loftb24">
                  <LoftOrdersPageB24 />
                </ProtectedRoute>
              }
            />

            <Route
              path="scladb24"
              element={
                <ProtectedRoute requiredPermission="scladb24">
                  <ScladOrdersPageB24 />
                </ProtectedRoute>
              }
            />

            <Route
              path="argonb24"
              element={
                <ProtectedRoute requiredPermission="argonb24">
                  <ArgonOrdersPageB24 />
                </ProtectedRoute>
              }
            />

            <Route 
              path="upakb24" 
              element={
                <ProtectedRoute requiredPermission="upakb24">
                  <UpakOrdersPageB24 />
                </ProtectedRoute>
              }
            />

            <Route 
              path="pickupb24" 
              element={
                <ProtectedRoute requiredPermission="pickupb24">
                  <PickupOrdersPageB24 />
                </ProtectedRoute>
              }
            />


            <Route 
              path="sendb24" 
              element={
                <ProtectedRoute requiredPermission="sendb24">
                  <SendOrdersPageB24 />
                </ProtectedRoute>
              }
            />

            <Route
              path="driverb24"
              element={
                <ProtectedRoute requiredPermission="driverb24">
                  <DriverSummaryPageB24 />
                </ProtectedRoute>
              }
            />

            <Route
              path="zakalka-driver-trips"
              element={
                <ProtectedRoute requiredPermission="driverb24">
                  <ZakalkaDriverTripsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="trip-management" 
              element={
                <ProtectedRoute requiredPermission="trip-management">
                  <TripManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="media-admin"
              element={
                <ProtectedRoute allowedRoles={['admin', 'adminb24']}>
                  <MediaAdminPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="news"
              element={
                <ProtectedRoute>
                  <NewsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="visualloft"
              element={
                <ProtectedRoute requiredPermission="visualloft">
                  <Visualloft />
                </ProtectedRoute>
              }
            />

            <Route 
              path="users" 
              element={
                <ProtectedRoute requiredPermission="users">
                  <UsersManagementPage />
                </ProtectedRoute>
              } 
            />

            <Route
              path="users/new"
              element={
                <ProtectedRoute requiredPermission="users" allowedRoles={['admin']}>
                  <UserEditorPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="users/:userId/edit"
              element={
                <ProtectedRoute requiredPermission="users" allowedRoles={['admin']}>
                  <UserEditorPage />
                </ProtectedRoute>
              }
            />

            <Route 
              path="orders-dashboard" 
              element={
                <ProtectedRoute requiredPermission="orders-dashboard">
                  <OrdersDashboardPage />
                </ProtectedRoute>
              } 
            />

            <Route
              path="file-print"
              element={
                <ProtectedRoute requiredPermission="file-print">
                  <FilePrintPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="order-form"
              element={
                <ProtectedRoute requiredPermission="order-form">
                  <OrderFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="reports"
              element={
                <ProtectedRoute requiredPermission="reports">
                  <ReportsMainPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="buhgalteriya"
              element={
                <ProtectedRoute requiredPermission="buhgalteriya">
                  <BuhgalteriyaPage />
                </ProtectedRoute>
              }
            />

            {/* Перенаправление для неизвестных маршрутов */}
            <Route path="*" element={<RoleBasedRedirect />} />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
